import React, { useContext } from 'react';
import { Box, Divider, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'oidc-react/build/src/useAuth';
import { UseQueryResult } from '@tanstack/react-query';
import { Channel, Notification, UserNotification } from '../../models/notifications';
import { AVAILABLE_CHANNELS } from '../../constants';
import {
  disableChannelNotifications,
  enableChannelNotifications,
  updateNotification,
} from '../../api/notifications';
import { SettingsContext } from '../../context/SettingsContext';


interface NotificationsSettingsContainerProps {
  description?: string;
  channel: Channel;
  onUpdateChannel: (updatedChannel: Channel) => void;
  showSnackbar: (success?: boolean) => void;
  refetch: UseQueryResult<UserNotification, unknown>['refetch']
}

const NotificationsSettingsContainer = ({
  description,
  channel,
  onUpdateChannel,
  showSnackbar,
  refetch
}: NotificationsSettingsContainerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { calendarPreferences } = useContext(SettingsContext);
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;

  const handleChangeChannel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIsEnabled = event.target.checked;

    const updatedChannel: Channel = {
      ...channel,
      isEnabled: newIsEnabled,
      notifications: channel.notifications.map((n) => ({
        ...n,
        isEnabled: newIsEnabled,
      })),
    };

    onUpdateChannel(updatedChannel);

    const apiCall = newIsEnabled
      ? enableChannelNotifications(
          {
            channelId: channel.id,
            businessHour: calendarPreferences.daysStart,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            settings: {},
          },
          maybeAccessToken
        )
      : disableChannelNotifications(channel.id, maybeAccessToken);

    apiCall.then(() => showSnackbar(true)).catch(() => showSnackbar()).finally(() => refetch());
  };

  const channelMustBeEnabled = (channelNotifications: Notification[]) =>
    channelNotifications.some((n) => n.isEnabled);

  const handleChangeNotification = (
    event: React.ChangeEvent<HTMLInputElement>,
    notificationId: number
  ) => {
    const newIsEnabled = event.target.checked;

    const newNotifications = channel.notifications.map((n) =>
      n.id === notificationId ? { ...n, isEnabled: newIsEnabled } : n
    );

    const updatedChannel: Channel = {
      ...channel,
      isEnabled: channelMustBeEnabled(newNotifications),
      notifications: newNotifications,
    };

    onUpdateChannel(updatedChannel);

    updateNotification(
      {
        channelId: channel.id,
        notificationTypeId: notificationId,
        isEnabled: newIsEnabled,
      },
      maybeAccessToken
    )
      .then(() => showSnackbar(true))
      .catch(() => showSnackbar())
      .finally(() => refetch());
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.grey['18p']}`,
        borderRadius: '8px',
        backgroundColor: 'background.paper',
      }}
    >
      {AVAILABLE_CHANNELS.includes(channel.displayKey) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Typography sx={{ marginLeft: '24px', marginY: '16px' }}>
            {description}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: { xs: '100%', md: '30%' },
                gap: 1,
                marginRight: { xs: '16px', md: '25px' },
                marginLeft: 'auto',
              }}
            >
              <Typography style={theme.typography.tooltipText}>
                {channel.isEnabled
                  ? t('screens.settings.enabled')
                  : t('screens.settings.disabled')}
              </Typography>
              <Switch
                checked={channel.isEnabled}
                onChange={handleChangeChannel}
                data-testid="channel-switch"
              />
            </Box>
          </Box>
        </Box>
      )}

      {AVAILABLE_CHANNELS.includes(channel.displayKey) &&
        channel.notifications?.map((n, i) => (
          <Box
            key={n.displayKey}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 1,
              marginRight: '16px',
            }}
          >
            <Divider
              sx={{
                borderTop: `1px solid ${theme.palette.grey['18p']}`,
                width: i === 0 ? '100%' : '97%',
                alignSelf: 'center',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '7px',
                marginRight: { md: '25px' },
              }}
            >
              <Typography variant="eventTitle" sx={{ marginRight: 'auto', paddingLeft: '24px' }}>
                {t(`screens.notifications.${n.displayKey}`)}
              </Typography>
              <Switch
                data-testid={`notification-switch-${n.displayKey}`}
                checked={n.isEnabled}
                onChange={(event) => handleChangeNotification(event, n.id)}
                sx={{
                  '& .MuiSwitch-track': {
                    backgroundColor: theme.palette.grey['12p'],
                    opacity: 1,
                  },
                }}
                disabled={!channel.isEnabled}
              />
            </Box>
          </Box>
        ))}
    </Box>
  );
};

export default NotificationsSettingsContainer;
